import { useEffect, useState } from 'react'

// 對話頁的讀寫面。🔴 這裡原本全是 GET；寫入只有兩個例外：#1445 的留意見，
// 與 #1640 的略過提問（只把等待回答的那一份補成未回答）。

export interface ConversationSummary {
  conversationId: string
  accountId: string
  membershipId: string
  villageId: string
  title: string | null
  lastActiveAt: string
  createdAt: string
  updatedAt: string
}

const getJson = async <T>(url: string): Promise<T> => {
  const res = await fetch(url)
  if (!res.ok) {
    // 錯誤訊息帶上狀態碼與 body：除錯用的頁面最不該做的事就是把失敗吞掉
    const body = await res.text()
    throw new Error(`${res.status} ${url}\n${body}`)
  }
  return (await res.json()) as T
}

// villageId 不再是查詢參數：伺服器一律用 cvt 的村里（#1287）。
export const fetchConversations = (limit: number) =>
  getJson<{ conversations: ConversationSummary[]; total: number }>(
    `/api/ai/conversations?limit=${limit}`,
  )

// 續談舊對話的讀取面：使用者訊息、助理回覆與工具呼叫，刻意不用 audit-view
// ——那條會連 systemPrompt 全文與 model_call 的 token 數一起吐出來。
export interface UserViewToolCall {
  toolName: string
  args: string
  result: string
  failed: boolean
  // 這一支執行了多久（毫秒）。null ＝ 那筆紀錄沒有量到，畫面上不顯示（#1399）
  elapsedMs: number | null
  // 四態判定：ok / failed / denied / needs_input（#1414）。後兩者不是失敗，
  // 畫面不標紅字。舊版伺服器沒有這欄，讀回 undefined 就退回 failed
  outcome?: string
  // 拒絕的說明或要問使用者的那一句；空字串或缺席＝沒有，畫面用固定文案
  outcomeMessage?: string
  // 真正的 toolCallId（#1640）。空字串或缺席＝舊事件或舊版伺服器，退回 eventId
  toolCallId?: string
  // 一組提問的回答狀態：pending / answered / unanswered；null 或缺席＝一般工具
  answerStatus?: string | null
  // 補上結果的時間（ISO）；null＝沒有
  resolvedAt?: string | null
  // 確認卡（#1693）：提出的參數、實際執行的參數、卡片呈現資料（JSON 字串）；空字串＝沒有
  proposedArgs?: string
  executedArgs?: string
  display?: string
}

export interface UserViewMessage {
  eventId: string
  // 這一則屬於第幾輪。JSON 本來就有，型別漏了（#1444）。🔴 錨點只能用它：
  // 訊息代號在即時是 SDK 產的隨機值，與重整後的事件代號毫無關係。
  turn: number
  role: 'user' | 'assistant'
  text: string
  aborted: boolean
  // 這一輪模型什麼都沒產出（#1473）。舊版伺服器沒有這欄，讀回 undefined＝不是那一種
  noOutput?: boolean
  // 有值 ＝ 這一則是工具呼叫而非文字訊息
  tool: UserViewToolCall | null
  // 僅 dev（#1739）：有值 ＝ 這一則是模型思考。非 dev 伺服器不回這種訊息
  thought?: { stepNumber: number; text: string } | null
}

export interface ConversationUserView {
  messages: UserViewMessage[]
  // 下一次送出要帶回去的 baseSequence。🔴 不要拿訊息的最大序號代替：
  // 有些事件不進這個視圖，那個值會落後，於是每一次送出都被判成衝突。
  lastSequence: number
  // 僅 dev（#1726）：這條對話最新一則訊息套用的模型，模型選單據此還原；非 dev 不回
  currentModel?: string
}

export const fetchConversationUserView = (conversationId: string) =>
  getJson<ConversationUserView>(
    `/api/ai/conversations/${encodeURIComponent(conversationId)}/user-view`,
  )

// 🔴 附件端點的前端讀取路徑已於 #1464 移除：工具直接把完整結果交給模型，
// 前端不再有人打它。端點本身的刪除是 #1465。

// 對話意見（#1445）。🔴 turn = 0 代表整份對話，1 起跳代表某一輪；
// 送出後不可編輯、不可刪除，要補充就再送一則。
export interface ConversationFeedback {
  feedbackId: string
  conversationId: string
  accountId: string
  villageId: string
  turn: number
  content: string
  createdAt: string
  updatedAt: string
}

const feedbackUrl = (conversationId: string): string =>
  `/api/ai/conversations/${encodeURIComponent(conversationId)}/feedback`

// 🔴 這個檔案裡唯一的 POST。純新增：同一輪送兩次就是兩則，不會覆寫前一則。
export const postConversationFeedback = async (
  conversationId: string, turn: number, content: string,
): Promise<ConversationFeedback> => {
  const res = await fetch(feedbackUrl(conversationId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ turn, content }),
  })
  if (!res.ok) throw new Error(`意見送出失敗（${res.status}）`)
  return (await res.json()) as ConversationFeedback
}

// 自己在這份對話留過的意見。送出後與重整後的回顯都讀它——
// 沒有唯一索引，回顯是唯一的防重複機制。
export const fetchMyConversationFeedback = (conversationId: string) =>
  getJson<{ feedback: ConversationFeedback[] }>(feedbackUrl(conversationId))

// 略過等待回答的提問（#1640）。不帶 toolCallId 由伺服器找出目前那一份——
// 提問面板沒畫出來時這是唯一的出口。失敗時帶上錯誤碼，呼叫端據此決定要不要重讀。
export interface SkipQuestionResult {
  toolCallIds: string[]
  status: 'unanswered'
  message: string
}

export class SkipQuestionError extends Error {
  constructor(message: string, readonly code: string) {
    super(message)
  }
}

const SKIP_FALLBACK_ERROR = '略過上方的問題失敗，這句話還沒送出，請再試一次。'
const SKIP_ALREADY_ANSWERED_ERROR = '上方的問題已經回答過了，已重新載入對話，這句話還沒送出。'

// reason 只影響確認卡寫給模型的字句（#1693）：cancel＝按了取消，interrupt＝直接打字
export const skipPendingQuestion = async (
  conversationId: string, toolCallId: string | null, reason?: 'cancel' | 'interrupt',
): Promise<SkipQuestionResult> => {
  const res = await fetch(
    `/api/ai/conversations/${encodeURIComponent(conversationId)}/questions/skip`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...(toolCallId ? { toolCallId } : {}), ...(reason ? { reason } : {}) }),
    },
  ).catch(() => null)
  if (res?.ok) return (await res.json()) as SkipQuestionResult
  const body = await res?.json().catch(() => null) as { code?: unknown } | null
  const code = typeof body?.code === 'string' ? body.code : ''
  // AI060003：那一份已經回答過（與 ask-user.ts 的 ALREADY_ANSWERED_CODE 同值）
  const message = code === 'AI060003' ? SKIP_ALREADY_ANSWERED_ERROR : SKIP_FALLBACK_ERROR
  throw new SkipQuestionError(message, code)
}

// 公文卡片的側邊預覽（#1620）。伺服器每次都重新檢查權限、模組與村里。
export interface DocumentPreviewFile {
  fileName: string
  format: string
  sizeBytes: number
}

export interface DocumentPreview {
  document: {
    documentId: string
    subject: string
    bureau: string
    issueNo: string
    issueDate: string
    tags: string[]
    content: string
    contentSummary: string
    files: DocumentPreviewFile[]
  }
  // 主前台這份公文的網址；伺服器沒設定時是 null，畫面就不顯示連結
  mainSiteUrl: string | null
}

// 錯誤代碼 → 給里長看的原因。🔴 與伺服器 error/error.ts 的代碼同值，由測試釘住。
export const DOCUMENT_PREVIEW_ERRORS: Record<string, string> = {
  AG050006: '你目前沒有文件管理的檢視權限，看不到這份公文。',
  AG050007: '你所在的單位沒有開通文件管理，看不到這份公文。',
  AG050008: '找不到這份公文，可能已被刪除，或不屬於你目前所在的單位。',
  AG050009: '此功能目前已停用。',
}

const PREVIEW_FALLBACK_ERROR = '公文預覽暫時載入失敗，請稍後再試。'

export const fetchDocumentPreview = async (documentId: string): Promise<DocumentPreview> => {
  const res = await fetch(`/api/ai/documents/${encodeURIComponent(documentId)}/preview`)
  if (res.ok) return (await res.json()) as DocumentPreview
  const body = await res.json().catch(() => null) as { code?: unknown } | null
  const code = typeof body?.code === 'string' ? body.code : ''
  const known = Object.prototype.hasOwnProperty.call(DOCUMENT_PREVIEW_ERRORS, code)
  throw new Error(known ? DOCUMENT_PREVIEW_ERRORS[code] : PREVIEW_FALLBACK_ERROR)
}

export interface Async<T> {
  data: T | null
  error: string | null
  loading: boolean
}

// 對話頁共用的載入狀態。deps 變動即重載；卸載後不 setState。
export const useAsync = <T>(load: () => Promise<T>, deps: unknown[]): Async<T> => {
  const [state, setState] = useState<Async<T>>({ data: null, error: null, loading: true })
  useEffect(() => {
    let alive = true
    setState({ data: null, error: null, loading: true })
    load()
      .then((data) => alive && setState({ data, error: null, loading: false }))
      .catch((e: Error) => alive && setState({ data: null, error: e.message, loading: false }))
    return () => {
      alive = false
    }
    // deps 由呼叫端給：load 是每次 render 新建的 closure，放進 deps 會無限重載
  }, deps)
  return state
}
